/**
 * Hook para verificar permissões do usuário
 */

import { useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";

export interface Permission {
  name: string;
  display_name: string;
  description: string;
}

export function usePermissions() {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    if (!user || typeof user !== "object") {
      return [];
    }

    // O tipo UserData pode não ter permissions, então fazemos um cast seguro
    const userWithPermissions = user as any;
    return (userWithPermissions.permissions || []) as Permission[];
  }, [user]);

  /**
   * Verifica se o usuário tem uma permissão específica
   */
  const hasPermission = (permissionName: string): boolean => {
    return permissions.some((p) => p.name === permissionName);
  };

  /**
   * Verifica se o usuário tem pelo menos uma das permissões fornecidas
   */
  const hasAnyPermission = (permissionNames: string[]): boolean => {
    return permissionNames.some((name) => hasPermission(name));
  };

  /**
   * Verifica se o usuário tem todas as permissões fornecidas
   */
  const hasAllPermissions = (permissionNames: string[]): boolean => {
    return permissionNames.every((name) => hasPermission(name));
  };

  /**
   * Verifica se o usuário é administrador
   * Verifica por permissão admin.full ou perfil Administrador
   */
  const isAdmin = useMemo(() => {
    if (!user || typeof user !== "object") {
      return false;
    }

    // Verificar se tem permissão admin.full
    const userWithPermissions = user as any;
    const userPermissions = (userWithPermissions.permissions || []) as Permission[];
    if (userPermissions.some((p) => p.name === "admin.full")) {
      return true;
    }

    // Verificar se o perfil é Administrador
    if (user.profile_name === "Administrador" || user.profile_id === "550e8400-e29b-41d4-a716-446655440001") {
      return true;
    }

    return false;
  }, [user, permissions]);

  /**
   * Verifica se o usuário é Administrador ou Suporte
   */
  const isAdminOrSupport = useMemo(() => {
    if (!user || typeof user !== "object") {
      return false;
    }

    // Verificar se tem permissão admin.full
    const userWithPermissions = user as any;
    const userPermissions = (userWithPermissions.permissions || []) as Permission[];
    if (userPermissions.some((p) => p.name === "admin.full")) {
      return true;
    }

    // Verificar se o perfil é Administrador
    if (user.profile_name === "Administrador" || user.profile_id === "550e8400-e29b-41d4-a716-446655440001") {
      return true;
    }

    // Verificar se o perfil é Suporte
    if (user.profile_name === "Suporte" || user.profile_id === "550e8400-e29b-41d4-a716-446655440004") {
      return true;
    }

    return false;
  }, [user, permissions]);

  // Helpers para CRUD por recurso (ex.: canDelete("clients") => verifica res_clients.delete)
  const canCreate = (resource: string): boolean => {
    return isAdmin || hasPermission(`res_${resource}.create`);
  };
  const canRead = (resource: string): boolean => {
    return isAdmin || hasPermission(`res_${resource}.read`);
  };
  const canUpdate = (resource: string): boolean => {
    return isAdmin || hasPermission(`res_${resource}.update`);
  };
  const canDelete = (resource: string): boolean => {
    return isAdmin || hasPermission(`res_${resource}.delete`);
  };

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isAdminOrSupport,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
  };
}

/**
 * Hook específico para verificar permissão de gerenciar modelos
 */
export function useCanManageModels() {
  const { hasPermission, isAdmin } = usePermissions();
  return isAdmin || hasPermission("manage_models");
}

/**
 * Hook específico para verificar permissão de gerenciar usuários
 */
export function useCanManageUsers() {
  const { hasPermission, isAdmin } = usePermissions();
  return isAdmin || hasPermission("manage_users");
}

/**
 * Hook específico para verificar permissão de gerenciar clientes
 */
export function useCanManageClients() {
  const { hasPermission, isAdmin } = usePermissions();
  return isAdmin || hasPermission("manage_clients");
}

/**
 * Hook específico para verificar permissão de gerenciar perfis
 */
export function useCanManageProfiles() {
  const { hasPermission, isAdmin } = usePermissions();
  return isAdmin || hasPermission("manage_profiles");
}

/**
 * Hook específico para verificar permissão de visualizar relatórios
 */
export function useCanViewReports() {
  const { hasPermission, isAdmin } = usePermissions();
  return isAdmin || hasPermission("view_reports");
}
